import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import intlTelInput from 'intl-tel-input';
import { FeeService } from '../service/fee.service';
import {
  MemberPersonalInfoUpdateRequest,
  MemberProfileResponse,
  MemberSubgroupInfo,
  UpdateEnrollmentDiscountRequest
} from '../interface/fee.interface';
import { DEFAULT_INITIAL_COUNTRY, DEFAULT_ITI_POLITE } from '../constants/global.constants';
import { ARM_PHONE_NUMBER_PATTERN } from '../constants/validation.constants';
import { DiscountType } from '../enums/DiscountType';
import {
  handleDiscountKeyDown,
  integerValidator,
  sanitizeDiscountPaste,
  syncDiscountControlErrors
} from '../utils/fee-discount.utils';
export interface EditPersonalInfoDialogData {
  memberId: number;
  profile: MemberProfileResponse;
  subgroup?: MemberSubgroupInfo;
}

@Component({
  selector: 'app-fee-edit-personal-info-dialog',
  templateUrl: './fee-edit-personal-info-dialog.component.html',
  styleUrls: ['./fee-edit-personal-info-dialog.component.scss']
})
export class FeeEditPersonalInfoDialogComponent implements OnInit, AfterViewInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();
  private readonly ARMENIA_ISO = '374';
  private discountTypeBeforeOpen = '';

  @ViewChild('phoneInput') phoneInputRef?: ElementRef<HTMLInputElement>;
  private iti: intlTelInput.Plugin | null = null;
  invalidPhoneMessage: string | null = null;
  phoneTouched = false;
  saveAttempted = false;

  form: FormGroup;
  saving = false;
  finalPrice = 0;

  readonly discountTypes = Object.values(DiscountType);

  readonly frequencyOptions = [
    { value: 'DAILY_SINGLE_PAY', labelKey: 'fee.frequency_types.DAILY_SINGLE_PAY' },
    { value: 'DAILY', labelKey: 'fee.frequency_types.DAILY' },
    { value: 'WEEKLY', labelKey: 'fee.frequency_types.WEEKLY' },
    { value: 'MONTHLY', labelKey: 'fee.frequency_types.MONTHLY' },
    { value: 'ANNUAL', labelKey: 'fee.frequency_types.ANNUAL' },
  ];

  constructor(
    private readonly dialogRef: MatDialogRef<FeeEditPersonalInfoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) readonly data: EditPersonalInfoDialogData,
    private readonly fb: FormBuilder,
    private readonly feeService: FeeService
  ) {
    const controls: Record<string, unknown> = {
      name: [data.profile.name || '', [Validators.minLength(2), Validators.maxLength(50)]],
      frequency: [{ value: data.profile.frequency || 'MONTHLY', disabled: true }],
      passportId: [data.profile.passportId || '', [Validators.pattern(/^[A-Za-z0-9]{0,20}$/)]],
      dateOfBirth: [data.profile.dateOfBirth ? new Date(data.profile.dateOfBirth) : null],
      phone: [data.profile.phone || '', Validators.required],
      email: [data.profile.email || '', [Validators.required, Validators.email]],
    };

    if (data.subgroup) {
      const sg = data.subgroup;
      controls['discountType'] = [sg.discountType ?? ''];
      controls['discountValue'] = [sg.discountValue || '', [Validators.min(0), integerValidator()]];
      controls['discountReason'] = [sg.discountReason ?? '', Validators.maxLength(30)];
    }

    this.form = this.fb.group(controls);
  }

  get showDiscountSection(): boolean {
    return !!this.data.subgroup;
  }

  get basePrice(): number {
    return this.data.subgroup?.price ?? 0;
  }

  ngOnInit(): void {
    if (!this.showDiscountSection) {
      return;
    }
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncDiscountErrors();
        this.recalculateFinalPrice();
      });
    this.recalculateFinalPrice();
  }

  ngAfterViewInit(): void {
    if (!this.phoneInputRef) return;
    this.iti = intlTelInput(this.phoneInputRef.nativeElement, {
      initialCountry: DEFAULT_INITIAL_COUNTRY,
      autoPlaceholder: DEFAULT_ITI_POLITE,
      separateDialCode: true,
      utilsScript: 'node_modules/intl-tel-input/build/js/utils.js'
    } as intlTelInput.Options);

    const existing = this.data.profile.phone;
    if (existing) {
      this.iti.setNumber(existing);
    }
  }

  ngOnDestroy(): void {
    this.iti?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  focusPhoneInput(): void {
    this.phoneInputRef?.nativeElement.focus();
  }

  preventDefaultIfNotNumber(event: KeyboardEvent): void {
    const allowedChars = /[0-9]/;
    if (!allowedChars.test(event.key) && event.key !== 'Backspace') {
      event.preventDefault();
    }
  }

  private get phoneInputValue(): string {
    return (this.phoneInputRef?.nativeElement.value ?? '').trim();
  }

  get isSaveDisabled(): boolean {
    if (this.saving) {
      return true;
    }
    const phone = this.resolvedPhoneValue();
    if (!phone || this.hasPhoneFormatError(phone)) {
      return true;
    }
    return this.isNonPhoneFormInvalid();
  }

  get showPhoneValidation(): boolean {
    return !!this.invalidPhoneMessage && (this.phoneTouched || this.saveAttempted);
  }

  private resolvedPhoneValue(): string {
    return this.phoneInputRef
      ? this.phoneInputValue
      : String(this.form.get('phone')?.value ?? '').trim();
  }

  private syncPhoneControl(): void {
    const phoneCtrl = this.form.get('phone');
    if (!phoneCtrl) {
      return;
    }
    phoneCtrl.setValue(this.resolvedPhoneValue(), { emitEvent: false });
    phoneCtrl.updateValueAndValidity({ emitEvent: false });
  }

  private hasPhoneFormatError(phone: string): boolean {
    if (!this.iti) {
      return false;
    }
    const dial = this.iti.getSelectedCountryData()?.dialCode;
    if (dial === this.ARMENIA_ISO) {
      return !ARM_PHONE_NUMBER_PATTERN.test(phone.replace(/\s+/g, ''));
    }
    return !this.iti.isValidNumber();
  }

  private isNonPhoneFormInvalid(): boolean {
    return Object.entries(this.form.controls)
      .filter(([name]) => name !== 'phone')
      .some(([, control]) => control.invalid);
  }

  get hasPhoneError(): boolean {
    const value = this.resolvedPhoneValue();
    if (!value) {
      return this.phoneTouched || this.saveAttempted;
    }
    return this.hasPhoneFormatError(value);
  }

  get isPhoneVisuallyInvalid(): boolean {
    return (this.phoneTouched || this.saveAttempted) && this.hasPhoneError;
  }

  validatePhoneNumber(): void {
    this.phoneTouched = true;
    this.syncPhoneControl();
    const phone = this.resolvedPhoneValue();
    if (!phone) {
      this.invalidPhoneMessage = 'fee.validation.phone_required';
      return;
    }
    this.invalidPhoneMessage = this.hasPhoneFormatError(phone)
      ? 'registration_content.valid_phone_number'
      : null;
  }

  onPhoneInput(): void {
    this.syncPhoneControl();
    if (!this.resolvedPhoneValue()) {
      this.invalidPhoneMessage = (this.phoneTouched || this.saveAttempted)
        ? 'fee.validation.phone_required'
        : null;
      return;
    }
    this.invalidPhoneMessage = null;
  }

  onDiscountKeyDown(event: KeyboardEvent): void {
    handleDiscountKeyDown(event);
  }

  onDiscountPaste(event: ClipboardEvent): void {
    this.form.get('discountValue')!.setValue(sanitizeDiscountPaste(event));
  }

  onDiscountSelectOpened(opened: boolean): void {
    if (opened) {
      this.discountTypeBeforeOpen = this.form.get('discountType')!.value || '';
    }
  }

  onDiscountTypeOptionClick(value: string): void {
    if (this.discountTypeBeforeOpen === value) {
      setTimeout(() => this.form.get('discountType')!.setValue(''));
    }
  }

  onReasonKeyDown(event: KeyboardEvent): void {
    if (event.key !== ' ') {
      return;
    }
    const input = event.target as HTMLInputElement;
    const caret = input.selectionStart ?? input.value.length;
    if (!input.value.slice(0, caret).trim()) {
      event.preventDefault();
    }
  }

  onReasonPaste(event: ClipboardEvent): void {
    this.form.get('discountReason')!.setValue(this.mergePastedValue(event).replace(/^\s+/, ''));
  }

  private mergePastedValue(event: ClipboardEvent): string {
    event.preventDefault();
    const input = event.target as HTMLInputElement;
    const pasted = event.clipboardData?.getData('text') ?? '';
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    return `${input.value.slice(0, start)}${pasted}${input.value.slice(end)}`;
  }

  private syncDiscountErrors(): void {
    syncDiscountControlErrors(
      this.form.get('discountType')!,
      this.form.get('discountValue')!,
      this.basePrice
    );
  }

  private recalculateFinalPrice(): void {
    const price = this.basePrice;
    const discountType: string = this.form.get('discountType')!.value;
    const discountValue: number = Number(this.form.get('discountValue')!.value) || 0;

    if (!discountValue || !discountType) {
      this.finalPrice = price;
      return;
    }
    if (discountType === DiscountType.PERCENT) {
      this.finalPrice = price - (price * discountValue / 100);
    } else {
      this.finalPrice = price - discountValue;
    }
  }

  private getFormattedPhone(): string {
    const value = this.resolvedPhoneValue();
    if (!value) return '';
    return this.iti?.getNumber() || value;
  }

  save(): void {
    this.saveAttempted = true;
    this.validatePhoneNumber();
    this.form.get('email')?.markAsTouched();
    this.form.get('phone')?.markAsTouched();
    if (this.isSaveDisabled) return;

    if (this.showDiscountSection) {
      this.syncDiscountErrors();
      const discountTypeCtrl = this.form.get('discountType')!;
      const discountValueCtrl = this.form.get('discountValue')!;
      if (discountTypeCtrl.invalid || discountValueCtrl.invalid) {
        discountTypeCtrl.markAsTouched();
        discountValueCtrl.markAsTouched();
        return;
      }
    }

    const raw = this.form.getRawValue();
    const normalizedEmail = typeof raw.email === 'string' ? raw.email.trim() : '';
    const phone = this.getFormattedPhone();
    const request: MemberPersonalInfoUpdateRequest = {
      name: raw.name || undefined,
      frequency: raw.frequency || undefined,
      passportId: raw.passportId || undefined,
      dateOfBirth: raw.dateOfBirth ? this.toDateString(raw.dateOfBirth) : undefined,
      phone,
      email: normalizedEmail,
    };
    this.saving = true;
    this.feeService.updateMemberPersonalInfo(this.data.memberId, request)
      .pipe(
        switchMap(profile => this.saveEnrollmentDiscount(profile)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: updated => this.dialogRef.close(updated),
        error: () => { this.saving = false; }
      });
  }

  private saveEnrollmentDiscount(profile: MemberProfileResponse): Observable<MemberProfileResponse> {
    const subgroup = this.data.subgroup;
    if (!subgroup) {
      return of(profile);
    }

    const discountValue = Number(this.form.get('discountValue')!.value) || 0;
    const discountType = this.form.get('discountType')!.value;
    const discountReason = (this.form.get('discountReason')!.value as string)?.trim() || null;
    const hasDiscount = discountValue > 0 && !!discountType;
    const hasReason = !!discountReason;

    if (!hasDiscount && !hasReason) {
      const hadOverride = !!(subgroup.discountType || subgroup.discountValue || subgroup.discountReason);
      if (!hadOverride) {
        return of(profile);
      }
    }

    const discountRequest: UpdateEnrollmentDiscountRequest = {
      discountType: hasDiscount ? discountType : null,
      discountValue: hasDiscount ? discountValue : null,
      discountReason,
    };

    return this.feeService.updateEnrollmentDiscount(this.data.memberId, subgroup.id, discountRequest);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private toDateString(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
}
